import { gql, TypedDocumentNode } from '@apollo/client'

import type { Item, ItemChildren, Project } from './project-details-types'

const CURRENT_CONTENT = `
  currentContent: content_version(args: { content_type: "current" }) {
    submodels
    external_metadata
    content
    qualityMetrics: quality_metrics
    comments
    inputs
  }
`

const SAVED_CONTENT = `
  savedContent: content_version(args: { content_type: "saved" }) {
    submodels
    external_metadata
    content
    qualityMetrics: quality_metrics
    comments
    inputs
  }
`

const ITEM = /* GraphQL */ `
  id
  createdAt: created_at
  updatedAt: updated_at
  status
  projectId: project_id
  contentType: content_type
  clonedFromId: cloned_from_id
  identifier
  title
  ${CURRENT_CONTENT}
  ${SAVED_CONTENT}
  originalContent: content_version(args: { content_type: "original" }) {
    submodels
  }
  job {
    id
    status
    ownerId: owner_id
    itemsRequired: items_required
    itemsPassed: items_passed
  }
  modelInfo: ai_model {
    id
    comments
    name
    qualityMetrics: quality_metrics
    type
    optionPrefix: public_mapper(path: "$.option_prefix")
    domain
    isInternal: is_internal
    inputSettings: input_settings
    internalName: internal_model_name
  }
  clones: clones_aggregate {
    aggregate {
      count
    }
  }
`

const ITEM_CHILDREN = /* GraphQL */ `
  children(where: { is_archived: { _eq: false } }) {
    id
    type: content_type
    ${CURRENT_CONTENT}
    ${SAVED_CONTENT}
    modelInfo: ai_model {
      optionPrefix: public_mapper(path: "$.option_prefix")
    }
  }
`

export const GET_PROJECT: TypedDocumentNode<
  {
    project: Project
  },
  {
    id: string
  }
> = gql`
  subscription project($id: uuid!) {
    project: project_by_pk(id: $id) {
      id
      name
      createdAt: created_at
      ownerId: owner_id
      hasAccess: has_access
      items(where: { is_archived: { _neq: true } }) {
        ${ITEM}
      }
    }
  }
`

const DRAFT_FILTERS = `
  project_id: { _is_null: true }
  created_at: { _gte: $draftDate }
  ai_model: { quality_metrics: { _is_null: false }, root_id: { _is_null: true } }
  job: { owner_id: { _eq: $userId }, status: { _neq: DISCARDED } }

  _or: [
    {
      content_versions: {
        content: { _neq: {} }
        quality_metrics: { _is_null: false, _neq: [] }
        type: { _nin: [FINAL_CONTENT] }
        status: { _eq: DONE }
      }
    }
    {
      _and: [
        {
          content_versions: {
            content: { _neq: {} }
            quality_metrics: { _eq: [] }
            type: { _nin: [FINAL_CONTENT] }
            status: { _eq: DONE }
          }
        }
        { job: { ai_model: { quality_metrics: { _eq: [] } } } }
      ]
    }
  ]
  is_archived: { _neq: true }
`

export const GET_DRAFT_ITEMS: TypedDocumentNode<
  {
    draft: Item[]
    total: { aggregate: { count: number } }
  },
  { draftDate: string; userId: string; offset: number; limit: number }
> = gql`
  query draft($draftDate: timestamptz!, $userId: String!, $offset: Int!, $limit: Int!) {
    draft: item(
      where: {
        ${DRAFT_FILTERS}
      }
      order_by: { created_at: desc }
      limit: $limit
      offset: $offset
    ) {
      ${ITEM}
    }

    total: item_aggregate(
      where: {
        ${DRAFT_FILTERS}
      }
      order_by: { created_at: desc }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const GET_ITEM_CHILDREN: TypedDocumentNode<{ item: ItemChildren }, { itemId: string }> = gql`
  query itemChildren($itemId: uuid!) {
    item: item_by_pk(id: $itemId) {
      id
      ${ITEM_CHILDREN}
    }
  }
`
